import React, { useState, useEffect, useCallback } from 'react';
import { boardsAPI } from '../../services/api';
import { formatPhone } from '../../utils/phone';
import './boards.css';

// A single board's Kanban. User-created columns; cards are entities placed on the
// board. Dragging a card moves it between columns (position only — no status/
// automation side effects). Add/rename/delete columns; add/remove cards.

const BoardView = ({ boardId, onBack, onOpenEntity }) => {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draggingCard, setDraggingCard] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [addCardFor, setAddCardFor] = useState(null); // columnId whose picker is open

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await boardsAPI.get(boardId);
      setBoard(res.data);
    } catch (err) {
      setError('Could not load this board.');
    } finally {
      setLoading(false);
    }
  }, [boardId]);

  useEffect(() => { load(); }, [load]);

  const onDrop = async (colId) => {
    setDragOverCol(null);
    const card = draggingCard;
    setDraggingCard(null);
    if (!card || card.fromColumnId === colId) return;
    // Optimistic: move locally to end of target column, then persist.
    setBoard((b) => {
      const columns = b.columns.map((c) => ({ ...c, cards: c.cards.filter((cd) => cd.cardId !== card.cardId) }));
      const target = columns.find((c) => c.id === colId);
      const moved = card.cardData;
      if (target) target.cards = [...target.cards, moved];
      return { ...b, columns };
    });
    try {
      await boardsAPI.moveCard(card.cardId, colId, 9999); // append; backend clamps
    } catch {
      setError('Could not move that card.');
      load();
    }
  };

  const addColumn = async () => {
    const name = window.prompt('New column name:');
    if (!name || !name.trim()) return;
    try {
      const res = await boardsAPI.addColumn(boardId, { name: name.trim() });
      setBoard((b) => ({ ...b, columns: [...b.columns, { ...res.data, cards: [] }] }));
    } catch {
      setError('Could not add the column.');
    }
  };

  const renameColumn = async (col) => {
    const name = window.prompt('Rename column:', col.name);
    if (!name || !name.trim()) return;
    try {
      await boardsAPI.updateColumn(col.id, { name: name.trim() });
      setBoard((b) => ({ ...b, columns: b.columns.map((c) => (c.id === col.id ? { ...c, name: name.trim() } : c)) }));
    } catch {
      setError('Could not rename the column.');
    }
  };

  const deleteColumn = async (col) => {
    if (!window.confirm(`Delete column "${col.name}" and its ${col.cards.length} card(s)? The underlying records are not deleted.`)) return;
    try {
      await boardsAPI.deleteColumn(col.id);
      setBoard((b) => ({ ...b, columns: b.columns.filter((c) => c.id !== col.id) }));
    } catch {
      setError('Could not delete the column.');
    }
  };

  const removeCard = async (colId, cardId) => {
    try {
      await boardsAPI.removeCard(cardId);
      setBoard((b) => ({
        ...b,
        columns: b.columns.map((c) => (c.id === colId ? { ...c, cards: c.cards.filter((cd) => cd.cardId !== cardId) } : c)),
      }));
    } catch {
      setError('Could not remove the card.');
    }
  };

  const onCardAdded = (colId, card) => {
    setAddCardFor(null);
    setBoard((b) => ({ ...b, columns: b.columns.map((c) => (c.id === colId ? { ...c, cards: [...c.cards, card] } : c)) }));
  };

  if (loading) return <div className="board-view-loading">Loading board…</div>;
  if (!board) return <div className="board-view-loading">{error || 'Board not found.'}</div>;

  return (
    <div className="board-view">
      <div className="board-view-header">
        <button className="board-back-btn" onClick={onBack}><i className="fas fa-arrow-left"></i></button>
        <span className="board-view-bar" style={{ background: board.color || '#6b7280' }} />
        <h2>{board.name}</h2>
        <span className="board-view-type">{board.type}</span>
        {error && <span className="board-view-error">{error}</span>}
      </div>

      <div className="board-view-columns">
        {board.columns.map((col) => (
          <div
            key={col.id}
            className={`bv-column${dragOverCol === col.id ? ' drag-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.id); }}
            onDragLeave={() => setDragOverCol((k) => (k === col.id ? null : k))}
            onDrop={() => onDrop(col.id)}
          >
            <div className="bv-col-header">
              <span className="bv-col-name">{col.name}</span>
              <span className="bv-col-count">{col.cards.length}</span>
              <div className="bv-col-actions">
                <button onClick={() => renameColumn(col)} title="Rename"><i className="fas fa-pen"></i></button>
                <button onClick={() => deleteColumn(col)} title="Delete"><i className="fas fa-trash"></i></button>
              </div>
            </div>

            <div className="bv-col-cards">
              {col.cards.map((card) => (
                <div
                  key={card.cardId}
                  className={`bv-card${draggingCard?.cardId === card.cardId ? ' dragging' : ''}${(board.type === 'Jobs' || board.type === 'Contacts' || (board.type === 'WorkOrders' && card.projectId)) ? ' clickable' : ''}`}
                  draggable
                  onDragStart={() => setDraggingCard({ cardId: card.cardId, fromColumnId: col.id, cardData: card })}
                  onDragEnd={() => setDraggingCard(null)}
                  onClick={() => {
                    if (!onOpenEntity) return;
                    // A work-order card's entityId is the work order, not the job — so
                    // it navigates by the projectId the card carries. Sending entityId
                    // would open whichever project happens to share that number.
                    if (board.type === 'WorkOrders') {
                      if (card.projectId) onOpenEntity(card.projectId, 'Jobs');
                      return;
                    }
                    if (board.type === 'Jobs' || board.type === 'Contacts') {
                      onOpenEntity(card.entityId, board.type);
                    }
                  }}
                >
                  <button className="bv-card-remove" title="Remove from board"
                    onClick={(e) => { e.stopPropagation(); removeCard(col.id, card.cardId); }}>×</button>
                  <div className="bv-card-title">{card.entity?.title || `#${card.entityId}`}</div>
                  {card.entity?.subtitle && <div className="bv-card-subtitle">{card.entity.subtitle}</div>}
                  {card.entity?.email && (
                    <a className="bv-card-contact" href={`mailto:${card.entity.email}`} onClick={(e) => e.stopPropagation()}>
                      <i className="fas fa-envelope"></i> {card.entity.email}
                    </a>
                  )}
                  {card.entity?.phone && (
                    <a className="bv-card-contact" href={`tel:${card.entity.phone}`} onClick={(e) => e.stopPropagation()}>
                      <i className="fas fa-phone"></i> {formatPhone(card.entity.phone)}
                    </a>
                  )}
                  {card.entity?.value != null && (
                    <div className="bv-card-value">${Number(card.entity.value).toLocaleString()}</div>
                  )}
                </div>
              ))}

              {addCardFor === col.id ? (
                <AddCardPicker boardId={boardId} columnId={col.id} onAdded={(c) => onCardAdded(col.id, c)} onClose={() => setAddCardFor(null)} />
              ) : (
                <button className="bv-add-card" onClick={() => setAddCardFor(col.id)}>
                  <i className="fas fa-plus"></i> Add card
                </button>
              )}
            </div>
          </div>
        ))}

        <button className="bv-add-column" onClick={addColumn}>
          <i className="fas fa-plus"></i> Add column
        </button>
      </div>
    </div>
  );
};

// Inline search picker to add an existing entity to a column as a card.
const AddCardPicker = ({ boardId, columnId, onAdded, onClose }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        const res = await boardsAPI.available(boardId, search);
        if (active) setResults(res.data || []);
      } catch { /* ignore */ }
    };
    run();
    return () => { active = false; };
  }, [boardId, search]);

  const add = async (entityId) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await boardsAPI.addCard(boardId, columnId, entityId);
      const picked = results.find((r) => r.entityId === entityId);
      onAdded({ cardId: res.data.cardId, entityId, position: res.data.position, entity: { title: picked?.title, subtitle: picked?.subtitle } });
    } catch (err) {
      alert(err.response?.data?.message || 'Could not add that item.');
      setBusy(false);
    }
  };

  return (
    <div className="bv-picker">
      <input autoFocus placeholder="Search to add…" value={search} onChange={(e) => setSearch(e.target.value)} />
      <div className="bv-picker-results">
        {results.length === 0 ? (
          <div className="bv-picker-none">No matches</div>
        ) : (
          results.map((r) => (
            <button key={r.entityId} className="bv-picker-item" onClick={() => add(r.entityId)}>
              <span className="bv-picker-title">{r.title}</span>
              {r.subtitle && <span className="bv-picker-sub">{r.subtitle}</span>}
            </button>
          ))
        )}
      </div>
      <button className="bv-picker-close" onClick={onClose}>Done</button>
    </div>
  );
};

export default BoardView;
